import PlotlyChart from './PlotlyChart'
import { resolveColumnRef, resolveValue, formatValue, getPlotlyAxisFormat, groupDataBySeries } from '../utils/chartDataUtils'
import { COLORS, mergeLayout, mergeConfig } from '../utils/chartTheme'

export default function ColumnChartV2({ data, chartOptions = {}, dimensionInfo = {} }) {
  const {
    columns = [],
    x = { source: 'time', kind: 'date', label: 'Time' },
    y = { source: 'count', label: 'Count' },
    seriesBy,
    title,
    orientation,
    stacked,
    layout: customLayout = {},
    config: customConfig = {}
  } = chartOptions

  const isHorizontal = orientation === 'h'

  const xColumn = resolveColumnRef(x, columns)
  const yRefs = Array.isArray(y) ? y : [y]

  let allTraces = []
  let yAxisConfig = {}

  if (yRefs.length > 1 && !seriesBy) {
    for (const yRef of yRefs) {
      const yColumn = resolveColumnRef(yRef, columns)
      const traces = buildBarTraces({ data, dimensionInfo, columns, x, y: yRef, seriesBy: null })
      for (const trace of traces) {
        trace.name = yColumn.label || yColumn.source
        allTraces.push(trace)
      }
      yAxisConfig = { ...getPlotlyAxisFormat(yColumn) }
    }
  } else {
    const yColumn = resolveColumnRef(yRefs[0], columns)
    allTraces = buildBarTraces({ data, dimensionInfo, columns, x, y: yRefs[0], seriesBy })
    yAxisConfig = { ...getPlotlyAxisFormat(yColumn) }
  }

  allTraces.forEach((trace, i) => {
    if (!trace.marker?.color) {
      trace.marker = { ...trace.marker, color: COLORS[i % COLORS.length] }
    }
    if (isHorizontal) {
      trace.orientation = 'h'
      // Swap x and y for horizontal bars
      const tmp = trace.x
      trace.x = trace.y
      trace.y = tmp
    }
  })

  const barmode = stacked
    ? 'stack'
    : (seriesBy || yRefs.length > 1 ? 'group' : 'relative')

  const xAxisConfig = isHorizontal
    ? { ...yAxisConfig }
    : {
        title: { text: xColumn.label, font: { size: 12, color: '#94a3b8' } },
        ...(xColumn.kind === 'date' ? { type: 'date' } : {}),
      }

  const yAxisLayoutConfig = isHorizontal
    ? {
        title: { text: xColumn.label, font: { size: 12, color: '#94a3b8' } },
        ...(xColumn.kind === 'date' ? { type: 'date' } : {}),
        automargin: true,
      }
    : { ...yAxisConfig }

  const layout = mergeLayout({
    ...(title ? { title: { text: title, font: { size: 15, color: '#334155' }, x: 0, xanchor: 'left', pad: { l: 4 } } } : {}),
    xaxis: xAxisConfig,
    yaxis: yAxisLayoutConfig,
    margin: { t: title ? 48 : 24, r: 16, b: 44, l: isHorizontal ? 120 : 52 },
    bargap: 0.15,
    barmode,
    ...customLayout,
  })

  const config = mergeConfig(customConfig)

  return (
    <PlotlyChart
      data={allTraces}
      useResizeHandler={true}
      layout={layout}
      config={config}
      style={{ width: '100%', height: '100%' }}
    />
  )
}

function buildBarTraces({ data, dimensionInfo, columns, x, y, seriesBy }) {
  if (!data || data.length === 0) return []

  const xColumn = resolveColumnRef(x, columns)
  const yColumn = resolveColumnRef(y, columns)
  const seriesColumn = seriesBy ? resolveColumnRef(seriesBy, columns) : null

  const groups = groupDataBySeries(data, seriesColumn, dimensionInfo)
  const traces = []

  for (const [seriesName, seriesData] of groups) {
    const sortedData = [...seriesData].sort((a, b) => {
      const aVal = resolveValue(a, xColumn, dimensionInfo)
      const bVal = resolveValue(b, xColumn, dimensionInfo)
      if (xColumn.kind === 'date' || (typeof aVal === 'string' && !isNaN(Date.parse(aVal)))) {
        return new Date(aVal) - new Date(bVal)
      }
      return 0
    })

    const xValues = sortedData.map(row => resolveValue(row, xColumn, dimensionInfo))
    const yValues = sortedData.map(row => resolveValue(row, yColumn, dimensionInfo))

    const hoverText = sortedData.map(row => {
      const xFormatted = formatValue(resolveValue(row, xColumn, dimensionInfo), xColumn)
      const yFormatted = formatValue(resolveValue(row, yColumn, dimensionInfo), yColumn)
      const parts = [
        xColumn.label ? `${xColumn.label}: ${xFormatted}` : xFormatted,
        yColumn.label ? `${yColumn.label}: ${yFormatted}` : yFormatted
      ]
      if (seriesColumn && seriesName !== 'all') {
        parts.unshift(seriesName)
      }
      return parts.join('<br>')
    })

    traces.push({
      x: xValues,
      y: yValues,
      type: 'bar',
      name: seriesName === 'all' ? (yColumn.label || 'Value') : seriesName,
      text: hoverText,
      hoverinfo: 'text',
      textposition: 'none',
    })
  }

  return traces
}
